import { SolidBlock } from "@/app/components/brand-scaffold";
import {
  type TypographySample,
  type TypographySampleKind,
  type TypographyTone,
  typographyModeSpecs,
} from "@/design-system/registry/playground-typography";

function assertNever(value: never): never {
  throw new Error(`Unhandled value: ${String(value)}`);
}

function sampleTitle(kind: TypographySampleKind): string {
  switch (kind) {
    case "hugeTitle":
      return "# HUGE TITLE";
    case "subtitle":
      return "## SUBTITLE";
    case "itemTitle":
      return "### ITEM TITLE";
    case "body":
      return "p BODY TEXT — 正文内容";
    case "tinyLabel":
      return "small TINY LABEL";
    default:
      return assertNever(kind);
  }
}

function sampleNode(sample: TypographySample, tone: TypographyTone) {
  const title = `【${sample.id}】${sampleTitle(sample.kind)}`;

  switch (sample.kind) {
    case "hugeTitle":
      return (
        <h4 className="text-4xl sm:text-6xl md:text-8xl font-black uppercase leading-none tracking-tighter">
          {title}
        </h4>
      );
    case "subtitle":
      return (
        <h4
          className={
            tone === "light"
              ? "text-2xl font-black uppercase mb-2 border-b-4 border-black pb-2 inline-block"
              : "text-2xl font-black uppercase mb-2 border-b-4 border-white pb-2 inline-block"
          }
        >
          {title}
        </h4>
      );
    case "itemTitle":
      return (
        <h4 className={tone === "light" ? "text-xl font-black uppercase mb-2" : "text-xl font-black uppercase mb-2 text-white"}>
          {title}
        </h4>
      );
    case "body":
      return <p className={tone === "light" ? "text-sm font-bold leading-relaxed text-black" : "text-sm font-bold leading-relaxed text-white"}>{title}</p>;
    case "tinyLabel":
      return (
        <span className={tone === "light" ? "text-xs font-black uppercase tracking-wider bg-black text-white px-2 py-1" : "text-xs font-black uppercase tracking-wider bg-white text-black px-2 py-1"}>
          {title}
        </span>
      );
    default:
      return assertNever(sample.kind);
  }
}

function noteClassName(tone: TypographyTone): string {
  return tone === "light" ? "mt-2 text-sm font-bold text-zinc-700 leading-relaxed" : "mt-2 text-sm font-bold text-zinc-400 leading-relaxed";
}

function typographyCardClassName(tone: TypographyTone): string {
  return tone === "light" ? "bg-white" : "bg-black";
}

function typographyTextClassName(tone: TypographyTone): string {
  return tone === "light" ? "text-black" : "text-white";
}

function dividerClassName(tone: TypographyTone): string {
  return tone === "light" ? "border-b-4 border-black pb-4 mb-4" : "border-b-4 border-white pb-4 mb-4";
}

export function PlaygroundTypographySection() {
  return (
    <div>
      <div className="border-l-8 border-black pl-4 mb-10">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-1">SECTION A</p>
        <h2 className="text-3xl font-black uppercase">文字排版系统</h2>
      </div>

      <div>
        <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【A.0】PAGE LAYOUT — 标题层级体系</h3>
        <div className="space-y-10">
          <div className="space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-zinc-400">【A.0.1】# — 页面大标题块（Section eyebrow + title + description）</p>
            <div className="flex flex-col items-start gap-3 mt-4">
              <div className="inline-block border-4 border-black bg-white px-6 py-4 text-6xl font-black uppercase shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                EYEBROW
              </div>
              <div className="text-2xl font-bold text-black">Section Title — 用于页面主标题</div>
              <div className="border-l-8 border-[#A3E635] pl-4">
                <p className="text-lg font-black text-zinc-900">Description — 补充说明，最多一到两句，左侧品牌绿竖线标注。</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-zinc-400">【A.0.2】## — 区块分隔标题（Section 内部分组时使用）</p>
            <div className="flex flex-col items-start gap-3 mt-4">
              <div className="inline-block border-4 border-black bg-white/40 backdrop-blur-sm px-6 py-4 text-4xl font-black uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                SECTION A
              </div>
              <div className="text-2xl font-bold text-black">文字排版系统</div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-black uppercase tracking-widest text-zinc-400">【A.0.3】### — 卡片/组件标题（Section 内各子模块标题）</p>
            <h3 className="text-xl font-black uppercase mt-4 border-b-4 border-black pb-2 inline-block">【A.x】COMPONENT HEAD — 组件标题</h3>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【A.1】TYPOGRAPHY — 字体层级基准</h3>
        <p className="font-bold text-zinc-500 mb-6">以下是用于排版的结构参照（纯说明文字不设编号）。</p>
        <div className="grid lg:grid-cols-2 gap-8">
          {typographyModeSpecs.map((spec) => (
            <SolidBlock key={spec.tone} bgClass={typographyCardClassName(spec.tone)} hoverClass="" className={typographyTextClassName(spec.tone)}>
              <div className="space-y-8">
                <div className={dividerClassName(spec.tone)}>
                  <h4 className="text-xl font-black uppercase">{spec.title}</h4>
                </div>
                {spec.samples.map((sample) => (
                  <div key={sample.id}>
                    {sampleNode(sample, spec.tone)}
                    <p className={noteClassName(spec.tone)}>
                      {sample.note} 对应 markdown <code>{sample.markdownTag}</code>
                    </p>
                  </div>
                ))}
              </div>
            </SolidBlock>
          ))}
        </div>
      </div>
    </div>
  );
}
